import { FC, useEffect, useMemo, useState } from "react";
import { Outlet } from "react-router-dom";
import { useSelector } from "react-redux";

import { AppShell, Container } from "@mantine/core";
import { useScreen } from "utils/hooks";

import NavbarCustom from "components/layouts/Navbar";
import HeaderCustom from "components/layouts/Header";
import { PageFooter } from "components/layouts/PageFooter";
import Loading from "components/ui/Loading/Loading";

import { RootState } from "configs/configureStore";
import { AbilityContext, buildAbilityForPermissions } from "configs/casl";
import { isFullAccessRole } from "components/layouts/Navbar/navbarConfig";

import classes from "./MainLayout.module.css";

const MainLayout: FC = () => {
  const [opened, setOpened] = useState<boolean>(true);
  const [isDisableButton, setIsDisableButton] = useState(true);

  const largeScreen = useScreen();
  const { roles, permissions, isSessionLoading } = useSelector(
    (state: RootState) => state.authSlice,
  );

  const ability = useMemo(() => {
    if (isFullAccessRole(roles)) {
      return buildAbilityForPermissions(["*:*"]);
    }
    return buildAbilityForPermissions(permissions);
  }, [roles, permissions]);

  useEffect(() => {
    if (!largeScreen) {
      setOpened(false);
      setIsDisableButton(false);
    } else {
      setIsDisableButton(false);
    }
    return setOpened(true);
  }, [largeScreen]);

  if (isSessionLoading) {
    return <Loading variant="fullscreen" visible />;
  }

  return (
    <AbilityContext.Provider value={ability}>
      <AppShell
        layout="alt"
        padding={0}
        header={{ height: 60 }}
        footer={{ height: 60 }}
        transitionDuration={220}
        transitionTimingFunction="ease"
        navbar={{
          width: opened ? 260 : 80,
          breakpoint: "md",
          collapsed: {
            mobile: !opened,
          },
        }}
      >
        <AppShell.Header>
          <HeaderCustom
            isOpen={opened}
            isDisableButton={isDisableButton}
            trigger={() => setOpened(!opened)}
          />
        </AppShell.Header>

        <AppShell.Navbar>
          <NavbarCustom
            isExpanded={opened}
            triggerBurger={() => !largeScreen && setOpened(!opened)}
          />
        </AppShell.Navbar>
        <AppShell.Main>
          <AppShell.Section className={classes.main}>
            <Outlet />
          </AppShell.Section>
        </AppShell.Main>
        <AppShell.Footer p="md" mt={50}>
          <Container fluid px="lg">
            <PageFooter />
          </Container>
        </AppShell.Footer>
      </AppShell>
    </AbilityContext.Provider>
  );
};

export default MainLayout;
